import type { PackVertical, TopTab } from '../routes/route';
import type { KnotState } from './Knot';

// R1a — the SINGLE source of truth for the primary nav. The bottom nav
// (phone) and the side rail (tablet/desktop) are two renderings of THIS
// model: the same tab destinations, the same active/gold rule, and the
// Knot as the central capture action. Neither renderer defines its own tab
// list — they both map over `props.destinations` and share isGoldTab/
// navHref, so a destination added or relabelled shows up in both without a
// second edit.
//
// Core hardening 2026-07-29, Item 8: `destinations` is now a required prop
// rather than a fixed module constant — lib/useNavDestinations.ts resolves
// it per-session (a pack-declared NavRolesPackConfig's own tab set, or
// DEFAULT_NAV_DESTINATIONS below when no pack config applies). Actor's own
// pack declares no navRoles config, so this is a pure plumbing change for
// it — always resolves to DEFAULT_NAV_DESTINATIONS, byte-identical to
// today's fixed NAV_DESTINATIONS.
export interface NavModelProps {
  active: TopTab;
  onNavigate: (tab: TopTab) => void;
  knotState: KnotState;
  onKnotPress: () => void;
  destinations: NavDestination[];
  /** Voice amplitude 0..1 while the Knot is listening — forwarded straight to
   *  the Knot renderer so the centre dot pulses to the REAL microphone rather
   *  than a synthesised amp. Undefined outside a live hold. */
  knotAmp?: number;
  /** Press-and-hold (push-to-talk) handlers from lib/useKnotPushToTalk. Held
   *  = the mic listens until release; a quick tap falls through to onKnotPress
   *  (the typed capture sheet). Optional so any renderer/test that only wants
   *  the tap behaviour keeps working unchanged. */
  onKnotPointerDown?: (e: PointerEvent) => void;
  onKnotPointerUp?: (e: PointerEvent) => void;
  onKnotPointerCancel?: (e: PointerEvent) => void;
  /** CV4 §2: the backstop for a pointer that vanished without up OR cancel
   *  — without it a hold whose capture is lost never ends. */
  onKnotLostPointerCapture?: (e: PointerEvent) => void;
  /** CV6b §4: what is NEW behind a tab, rendered as a count on the
   *  destination itself. Keyed by tab, on the SHARED model, so the bottom
   *  bar and the rail cannot disagree about it — a per-renderer badge prop
   *  is exactly the drift this model exists to prevent. Absent or 0 renders
   *  nothing: an empty badge is noise. */
  badges?: Partial<Record<TopTab, TabBadge>>;
  /** A3: opens the More sheet. Present ONLY when the phone rail actually
   * overflowed (splitPhoneRail returned somewhere to go) — a More door to
   * nothing is worse than no door, so its absence is how the bar says
   * everything fits. The side rail never receives it: a wide screen renders
   * every group. */
  onMore?: () => void;
  /** True while that sheet is open, so the door reads as the active
   * destination rather than the tab underneath it. */
  moreActive?: boolean;
}

/** A tab's badge. `capped` carries CV6a's own honesty: the server stops
 *  counting at 99 and says so, so the label reads "99+" and never a
 *  truncated integer that looks exact. */
export interface TabBadge {
  count: number;
  capped: boolean;
}

/** What a badge says — or null when it says nothing. */
export function badgeLabel(badge: TabBadge | undefined): string | null {
  if (!badge || badge.count <= 0) return null;
  return badge.capped ? `${badge.count}+` : String(badge.count);
}

export interface NavDestination {
  tab: TopTab;
  /** Full label — used by the side rail, where there's room. */
  label: string;
  /** Space-tight variant for the phone bottom bar (e.g. "Audit'ns"). */
  short: string;
  /** A3: what that group is CALLED, as the pack declared it. The rail used to
   * print `group.slice(0, 3)` — so this pack's three blessed groups rendered
   * as "HOU / LED / CRA" while their real labels ("Household", "Money &
   * paperwork", "Craft") sat unused in the server's answer. Absent on flat
   * resolutions, where the rail renders no group labels at all. */
  groupLabel?: string;
  /** S1 (wf02 §01b): which nav-role GROUP this destination belongs to,
   *  when the pack resolved the session to grouped destinations (a
   *  multi-role membership's rail is the union of its groups — no mode
   *  switcher, no hidden tab). Absent on DEFAULT_NAV_DESTINATIONS and on
   *  single-flat `navTabs` resolutions — the side rail renders its
   *  original ungrouped layout then, byte-identical to before S1. */
  group?: string;
}

// Order matches the locked 5-slot shell: Today · Auditions · [Knot] ·
// Projects · Career. The bottom nav splits this list around the centred
// Knot (first two left, last two right); the rail lays it out top-to-bottom
// with the Knot in the same middle position. Actor's own default — the
// literal values are unchanged from the old NAV_DESTINATIONS constant.
//
// ⚑ IT IS THE JULY ACTOR PACK'S RAIL, AND ONLY ITS RAIL (2026-09-01). This
// was the app-wide fallback until then — the tabs every CallToWrap and every
// cockpit load rendered before `GET /api/team/me` answered, and the tabs they
// kept if it never did. Reach it through `defaultRailFor` below, which is
// where the "whose rail is this" question is actually asked.
export const DEFAULT_NAV_DESTINATIONS: NavDestination[] = [
  { tab: 'today', label: 'Today', short: 'Today' },
  { tab: 'auditions', label: 'Auditions', short: "Audit'ns" },
  { tab: 'projects', label: 'Projects', short: 'Projects' },
  { tab: 'career', label: 'Career', short: 'Career' },
];

/** The rail a pack gets when the server has confirmed it declares none.
 *
 * Only the July pack has one: its own tabs, unchanged, because it is the pack
 * that ships no NavRolesPackConfig and always resolved to exactly this. Every
 * other pack declares its rail, so "declares nothing" is not a case they have
 * — and an empty bar (the Knot alone, which the cockpit already renders) is
 * the honest answer rather than somebody else's tabs. */
export function defaultRailFor(vertical: PackVertical): NavDestination[] {
  return vertical === 'actors' ? DEFAULT_NAV_DESTINATIONS : [];
}

/** Where the Knot sits when a destination list is split into a
 *  before-Knot and after-Knot half (bottom nav) or interleaved (rail).
 *  Was a fixed constant (2) when the list was always DEFAULT_NAV_
 *  DESTINATIONS' 4 items — now a function of the actual list length, since
 *  a pack-declared nav set isn't necessarily 4 items. For
 *  DEFAULT_NAV_DESTINATIONS (length 4) this still evaluates to 2,
 *  identical to today — see useNavDestinations.test.ts's own pinned
 *  assertion. */
export function knotSlotIndex(destinations: NavDestination[]): number {
  return knotSlotFor(destinations.length);
}

/** The same answer from a COUNT rather than a list — because the phone bar's
 * slot count is not its destination count when a More door is present, and
 * splitting on the destinations alone is what pushed the Knot off centre. */
export function knotSlotFor(slots: number): number {
  return Math.floor(slots / 2);
}

/** S5a — WHICH NAV ROLE THE SERVER LANDED THIS MEMBER ON.
 *
 * The shell needs this to answer one question wf03 forces: a tech does not
 * get the office frame. Role decides the frame; the viewport only decides
 * the sizing.
 *
 * It is READ, never re-derived. `GET /api/team/me` resolves the union of
 * groups a membership holds (Core's `navGroupsForMembership`) with the
 * LANDING group first — that first group's key IS `resolveNavRole`'s answer,
 * so this is a lookup, not a second opinion. A member who holds dispatch and
 * tech both lands on dispatch and gets the office frame, which is correct:
 * they are an office person who also turns a wrench.
 *
 * Undefined when the server answered with the flat `navTabs` only (no group
 * keys) or with nothing at all — in which case the shell keeps the frame it
 * has always chosen off the viewport. Absence of an answer is not an answer.
 */
export function primaryNavRole(destinations: NavDestination[]): string | undefined {
  return destinations[0]?.group;
}

/** Where a nav destination GOES. The anchors below are REAL LINKS: they
 * preventDefault and call onNavigate on a plain click, but a middle-click, a
 * copied link or an open-in-new-tab uses the href, and an href that went
 * somewhere the click does not is a control lying about its destination.
 *
 * ⚑ 2026-09-19 — used to carry an optional subject scope (a performer's
 * career the app was standing in); the scope is deleted, so this is always
 * just the tab's own bare route. */
export function navHref(tab: TopTab): string {
  return `#${tab === 'today' ? '/' : `/${tab}`}`;
}

// Career is the one distinct "mode" — it lights GOLD when active; every
// other tab uses the ordinary active colour. Nothing else in the nav ever
// takes gold (the Knot excepted — it's gold by its own nature).
export function isGoldTab(tab: TopTab): boolean {
  return tab === 'career';
}

/* ------------------------------------------------------------------ *
 * A3 — WHEN A DECLARED RAIL DOES NOT FIT A PHONE.
 *
 * Blessed by Huan 2026-08-19, on the evidence of a screenshot: the
 * actor_assist pack declares NINE destinations in three groups, and at 390px
 * the bottom bar rendered them as one unreadable string with the first and
 * last clipped off the screen edges.
 *
 * THE RULE IS ABOUT THE DATA, NOT ABOUT A PACK. No `actor_assist` case
 * appears below, deliberately — the pack's own file makes the same argument
 * about itself ("there is no `parent` case in the engine; there never had to
 * be"). Any grouped rail with more destinations than a thumb row holds gets
 * the same treatment, which is also why Rexx's own office rail stops
 * overflowing on a phone as a consequence rather than as a second fix.
 *
 * WHAT STAYS IN THE BAR is the LANDING group — the one the server resolved
 * this membership to, first in the list. That is not an editorial choice
 * about which tabs matter: the pack already says these two things are
 * different. `household` is a nav ROLE you land on; `ledger` and `craft`
 * match no job role at all and exist only to be included. The bar shows
 * where you land; More holds what you were lent.
 *
 * THE RAIL IS UNTOUCHED. On a wide screen every group renders, grouped and
 * labelled, exactly as before — nothing here is called from that path.
 */

/** SLOTS in the phone bottom bar — destinations plus the More door, if
 * there is one.
 *
 * FOUR, because the shell is FOUR AROUND THE KNOT. That is not an arbitrary
 * cap: the bottom bar splits its slots into a left half and a right half with
 * the Knot between them, so an ODD number of slots cannot be balanced and the
 * Knot visibly drifts off centre. It did exactly that on the first cut of
 * this — four destinations plus a More door is five slots, which splits 2|3,
 * and Huan caught it on the screenshot ("it moves the knot away from the
 * center"). The Knot is the identity of this product; it sits in the middle.
 *
 * So when a rail overflows, MORE TAKES ONE OF THE FOUR rather than being
 * added beside them, and the bar keeps the exact geometry every other build
 * has. */
export const PHONE_BAR_SLOTS = 4;

export interface PhoneRail {
  /** What the bar renders. */
  shown: NavDestination[];
  /** What More opens. Empty when everything fits, and the More door is then
   * not rendered at all — a door to nothing is worse than no door. */
  overflow: NavDestination[];
}

/**
 * Splits a resolved rail into the thumb row and what sits behind More.
 *
 * Returns everything in `shown` — and an empty `overflow` — whenever the rail
 * fits or carries no groups at all. That covers every pack that existed
 * before this function did, which is what makes this safe to apply
 * unconditionally rather than behind a flag someone has to remember.
 */
export function splitPhoneRail(destinations: NavDestination[]): PhoneRail {
  if (destinations.length <= PHONE_BAR_SLOTS) return { shown: destinations, overflow: [] };

  const landing = primaryNavRole(destinations);
  // A rail with no group data cannot name a landing group, so the honest cut
  // is simply the front of the list — the order the pack declared.
  const inLanding = landing ? destinations.filter((d) => d.group === landing) : destinations;

  // MORE OCCUPIES THE LAST SLOT, so three destinations remain. Which three is
  // the pack's own declaration order, not an opinion here about which of a
  // parent's tabs matters least — and the fourth is not hidden, it is one tap
  // away inside More, under its own group's heading.
  const shown = inLanding.slice(0, PHONE_BAR_SLOTS - 1);
  const shownTabs = new Set(shown.map((d) => d.tab));
  return { shown, overflow: destinations.filter((d) => !shownTabs.has(d.tab)) };
}
